/**
 * Director ledgers: what the company owes each director, under 2530.
 *
 * One ledger per director, coded 2530-001, 2530-002 ... A sub-ledger of the
 * control account 2530 Loans from Directors, so the strip at the top checks
 * that the directors add up to it. A director is added here (not on Vendors),
 * by the Book Keeper, and approved once by the Finance Head; only then can a
 * bank line name them.
 *
 * A director's opening balance -- what the company owed them when the books
 * began -- is raised on their panel by the Book Keeper, approved by a Finance
 * Head, and then posted by the Book Keeper. It is a journal, so it takes the
 * journal's approval (two Finance Heads above that rule's threshold).
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

interface Director {
  id: string
  code: string
  name: string
  legal_name: string | null
  pan: string | null
  status: string
  label: string
  opening_paise: number
  received_paise: number
  repaid_paise: number
  balance_paise: number
  lines: number
  raised_by: string | null
}

interface Book {
  account_code: string | null
  account_name: string | null
  date_from: string | null
  date_to: string
  directors: Director[]
  control_paise: number
  directors_paise: number
  unassigned_paise: number
  difference_paise: number
}

interface HistoryLine {
  //: The opening balance, brought in from before the books began.
  opening: boolean
  date: string
  journal_number: string
  narration: string
  received_paise: number
  repaid_paise: number
  balance_paise: number
  bank_reference: string | null
}

interface Opening {
  state: 'none' | 'with_finance_head' | 'approved' | 'posted' | 'sent_back'
  journal_id: string | null
  reference: string | null
  amount_paise: number | null
  as_at: string | null
  basis: string | null
  raised_by: string | null
  explain: string | null
  approvals_required: number
  approvals_valid: number
  sent_back_reason: string | null
  journal_number: string | null
  latest_as_at: string | null
  earliest_as_at: string | null
}

type Tab = 'active' | 'waiting' | 'all'

const STATUS_TONE: Record<string, string> = {
  approved: 'approved',
  submitted: 'submitted',
  draft: 'draft',
  returned: 'returned',
  inactive: 'draft',
}

const STATUS_LABEL: Record<string, string> = {
  approved: 'active',
  submitted: 'with Finance Head',
  draft: 'draft',
  returned: 'sent back',
  inactive: 'closed',
}

export default function DirectorLedgers() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  //: Unset until somebody picks one: an approver with ledgers waiting lands on them.
  const [picked, setTab] = useState<Tab | null>(null)
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const book = useQuery({
    queryKey: ['director-ledgers', from, to],
    queryFn: () =>
      api.get<Book>(
        `/directors?${new URLSearchParams({
          ...(from ? { date_from: from } : {}),
          ...(to ? { date_to: to } : {}),
        }).toString()}`,
      ),
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['director-ledgers'] })

  const rows = book.data?.directors ?? []
  const tab: Tab =
    picked ??
    (can('party.vendor.approve') && rows.some((r) => r.status === 'submitted')
      ? 'waiting'
      : 'active')
  const shown = rows.filter(
    (r) =>
      (tab === 'all' || (tab === 'active' ? r.status === 'approved' : r.status !== 'approved')) &&
      matches(search, r.code, r.name, r.legal_name),
  )
  const paged = usePaged(shown, `${tab}|${search}`)
  const opened = rows.find((r) => r.id === open) ?? null

  if (book.isLoading) return <Loading what="Loading director ledgers" />
  const b = book.data
  const waiting = rows.filter((r) => r.status !== 'approved' && r.status !== 'inactive')

  return (
    <>
      <ErrorNote error={book.error} />

      {can('party.vendor.create') && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => setAdding(true)}>
            Add a director
          </button>
        </div>
      )}

      {b && (
        <div className="grid">
          <Stat
            label={`${b.account_code ?? '2530'} ${b.account_name ?? 'Loans from directors'}`}
            value={formatInr(b.control_paise)}
            note="what the company owes its directors"
          />
          <Stat
            label="Directors' ledgers"
            value={formatInr(b.directors_paise)}
            note={`${rows.filter((r) => r.status === 'approved').length} active ledgers`}
          />
          <Stat
            label="Difference"
            value={formatInr(b.difference_paise + b.unassigned_paise)}
            note={
              b.difference_paise + b.unassigned_paise === 0
                ? 'the ledgers add up to 2530'
                : 'part of 2530 names no director — look into it'
            }
          />
          <Stat
            label="Waiting"
            value={String(waiting.length)}
            note="draft, sent back or with the Finance Head"
          />
        </div>
      )}

      {b && b.unassigned_paise !== 0 && (
        <Note kind="warn">
          {formatInr(b.unassigned_paise)} on {b.account_code} names no director ledger. Every line
          on this account should name one; this is shown, never corrected automatically.
        </Note>
      )}

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              {
                key: 'active',
                label: 'Active',
                count: rows.filter((r) => r.status === 'approved').length,
              },
              {
                key: 'waiting',
                label: 'Not active',
                count: rows.length - rows.filter((r) => r.status === 'approved').length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Code or director"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search director ledgers"
            />
            <label className="appr-check">
              From
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="appr-check">
              To
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No director ledgers yet. Add each director the company borrows from or repays.'
                : 'No director ledger matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Director</th>
                  <th className="num">Opening</th>
                  <th className="num">Received</th>
                  <th className="num">Repaid</th>
                  <th className="num">Owed now</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.id} {...rowProps(() => setOpen(r.id), open === r.id)}>
                    <td>
                      <div className="strong">{r.name}</div>
                      <div className="cell-sub ref">{r.code}</div>
                    </td>
                    <td className="num">{formatInr(r.opening_paise)}</td>
                    <td className="num">{formatInr(r.received_paise)}</td>
                    <td className="num">{formatInr(r.repaid_paise)}</td>
                    <td className="num strong">{formatInr(r.balance_paise)}</td>
                    <td>
                      <span className={`pill ${STATUS_TONE[r.status] ?? 'draft'}`}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {adding && (
        <AddDirector
          onClose={() => setAdding(false)}
          onDone={(id) => {
            refresh()
            setAdding(false)
            setOpen(id)
          }}
        />
      )}
      {opened && (
        <DirectorPanel
          director={opened}
          from={from}
          to={to}
          onClose={() => setOpen(null)}
          onChanged={refresh}
        />
      )}
    </>
  )
}

function AddDirector({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [form, setForm] = useState({ name: '', legal_name: '', pan: '' })
  const [distinct, setDistinct] = useState(false)
  const create = useMutation({
    mutationFn: () =>
      api.post<Director>('/directors', {
        name: form.name,
        legal_name: form.legal_name || null,
        pan: form.pan || null,
        confirm_distinct: distinct,
      }),
    onSuccess: (d) => onDone(d.id),
  })
  const clash = create.error instanceof Error && /already has a ledger/.test(create.error.message)
  return (
    <SidePanel
      kicker="Director ledger"
      title="Add a director"
      sub="a ledger of their own under 2530 Loans from Directors"
      onClose={onClose}
    >
      <ErrorNote error={create.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <Field label="Name" caption="as you know them, e.g. Dr. Madhavi Joshi">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={200}
          />
        </Field>
        <Field label="Legal name" caption="as on PAN or the bank account, if different">
          <input
            value={form.legal_name}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
            maxLength={200}
          />
        </Field>
        <Field label="PAN" caption="optional">
          <input
            className="mono"
            value={form.pan}
            onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
            maxLength={10}
          />
        </Field>
        {clash && (
          <label className="appr-check" style={{ margin: '8px 0' }}>
            <input
              type="checkbox"
              checked={distinct}
              onChange={(e) => setDistinct(e.target.checked)}
            />
            This is a different person with the same name
          </label>
        )}
        <p className="muted" style={{ fontSize: 14 }}>
          The ledger gets the next code (2530-001, 2530-002 …). Submit it for the Finance Head to
          approve; a bank line can name the director once it is approved.
        </p>
        <div className="actions">
          <button className="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add director'}
          </button>
        </div>
      </form>
    </SidePanel>
  )
}

function DirectorPanel({
  director: d,
  from,
  to,
  onClose,
  onChanged,
}: {
  director: Director
  from: string
  to: string
  onClose: () => void
  onChanged: () => void
}) {
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    name: d.name,
    legal_name: d.legal_name ?? '',
    pan: d.pan ?? '',
  })
  const [reason, setReason] = useState('')
  const [closing, setClosing] = useState(false)
  const [sendBack, setSendBack] = useState('')

  const history = useQuery({
    queryKey: ['director-history', d.id, from, to],
    queryFn: () =>
      api.get<{ opening_paise: number; lines: HistoryLine[] }>(
        `/directors/${d.id}/history?${new URLSearchParams({
          ...(from ? { date_from: from } : {}),
          ...(to ? { date_to: to } : {}),
        }).toString()}`,
      ),
  })
  const act = useMutation({
    mutationFn: (v: { path: string; body?: unknown; method?: 'post' | 'patch' }) =>
      v.method === 'patch' ? api.patch(v.path, v.body) : api.post(v.path, v.body),
    onSuccess: () => {
      setEditing(false)
      setClosing(false)
      setReason('')
      setSendBack('')
      onChanged()
    },
  })
  const approved = d.status === 'approved'
  const editable = d.status === 'draft' || d.status === 'returned' || approved

  return (
    <SidePanel
      kicker={`Director ledger · ${d.code}`}
      title={d.name}
      sub={d.legal_name && d.legal_name !== d.name ? d.legal_name : undefined}
      amount={formatInr(d.balance_paise)}
      badge={
        <span className={`pill ${STATUS_TONE[d.status] ?? 'draft'}`}>
          {STATUS_LABEL[d.status] ?? d.status}
        </span>
      }
      wide
      onClose={onClose}
      foot={
        <>
          {(d.status === 'draft' || d.status === 'returned' || d.status === 'inactive') &&
            can('party.vendor.submit') && (
              <button
                className="primary"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: `/parties/${d.id}/submit` })}
              >
                {d.status === 'inactive' ? 'Submit to reopen' : 'Submit for approval'}
              </button>
            )}
          {d.status === 'submitted' && can('party.vendor.approve') && (
            <>
              <button
                className="primary"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: `/parties/${d.id}/approve` })}
              >
                Approve
              </button>
              <input
                aria-label="What needs correcting"
                placeholder="What needs correcting?"
                value={sendBack}
                onChange={(e) => setSendBack(e.target.value)}
              />
              <button
                disabled={act.isPending || !sendBack.trim()}
                onClick={() =>
                  act.mutate({ path: `/parties/${d.id}/return`, body: { reason: sendBack } })
                }
              >
                Send back
              </button>
            </>
          )}
          {editable && can('party.vendor.edit') && !editing && (
            <button className="quiet" onClick={() => setEditing(true)}>
              Correct details
            </button>
          )}
          {approved && can('party.vendor.edit') && !closing && (
            <button className="quiet" onClick={() => setClosing(true)}>
              Close ledger
            </button>
          )}
        </>
      }
    >
      <ErrorNote error={act.error ?? history.error} />
      <dl className="appr-facts">
        <div>
          <dt>Code</dt>
          <dd className="mono">{d.code}</dd>
        </div>
        <div>
          <dt>PAN</dt>
          <dd className="mono">{d.pan ?? '—'}</dd>
        </div>
        <div>
          <dt>Opening</dt>
          <dd>{formatInr(d.opening_paise)}</dd>
        </div>
        <div>
          <dt>Received · repaid</dt>
          <dd>
            {formatInr(d.received_paise)} · {formatInr(d.repaid_paise)}
          </dd>
        </div>
        <div>
          <dt>Raised by</dt>
          <dd>{d.raised_by ?? '—'}</dd>
        </div>
      </dl>
      {d.status === 'submitted' && (
        <Note kind="info">
          With the Finance Head. It can be chosen on a bank line once it is approved.
        </Note>
      )}

      {editing && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            act.mutate({
              path: `/directors/${d.id}`,
              method: 'patch',
              body: {
                name: form.name,
                legal_name: form.legal_name,
                pan: form.pan,
                reason: reason || null,
              },
            })
          }}
        >
          <div className="row">
            <Field label="Name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <Field label="Legal name">
              <input
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              />
            </Field>
            <Field label="PAN" caption={approved ? 'the Finance Head changes this' : 'optional'}>
              <input
                className="mono"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                maxLength={10}
              />
            </Field>
          </div>
          {approved && (
            <Field label="Why" caption="required once the ledger is approved; kept in the history">
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          )}
          <div className="actions">
            <button className="primary" type="submit" disabled={act.isPending}>
              Save
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {closing && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            act.mutate({
              path: `/directors/${d.id}`,
              method: 'patch',
              body: { deactivate: true, reason },
            })
          }}
        >
          <Field
            label="Why is this ledger being closed?"
            caption="new bank lines can no longer name this director; the history stays"
          >
            <input value={reason} onChange={(e) => setReason(e.target.value)} required />
          </Field>
          <div className="actions">
            <button className="primary" type="submit" disabled={act.isPending || !reason.trim()}>
              Close ledger
            </button>
            <button type="button" onClick={() => setClosing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {approved && <OpeningBalance director={d} onChanged={onChanged} />}

      <h3 style={{ margin: '16px 0 8px' }}>Ledger</h3>
      {history.isLoading ? (
        <Loading what="Loading the ledger" />
      ) : (history.data?.lines ?? []).length === 0 ? (
        <div className="empty">Nothing posted to this ledger yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="appr-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry</th>
                <th className="num">Received</th>
                <th className="num">Repaid</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={4} className="muted">
                  Opening
                </td>
                <td className="num">{formatInr(history.data?.opening_paise ?? 0)}</td>
              </tr>
              {(history.data?.lines ?? []).map((l) => (
                <tr key={`${l.journal_number}-${l.date}`}>
                  <td className="date">{l.date}</td>
                  <td>
                    <div className="ref">
                      {l.journal_number}
                      {l.opening && <span className="pill approved">opening balance</span>}
                    </div>
                    <div className="cell-sub mono clipped" title={l.bank_reference ?? l.narration}>
                      {l.bank_reference ?? l.narration}
                    </div>
                  </td>
                  <td className="num">
                    {l.received_paise && !l.opening ? formatInr(l.received_paise) : ''}
                  </td>
                  <td className="num">{l.repaid_paise ? formatInr(l.repaid_paise) : ''}</td>
                  <td className="num strong">{formatInr(l.balance_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SidePanel>
  )
}

function OpeningBalance({ director: d, onChanged }: { director: Director; onChanged: () => void }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ amount: '', as_at: '', basis: '' })
  const [sendBack, setSendBack] = useState('')

  const opening = useQuery({
    queryKey: ['director-opening', d.id],
    queryFn: () => api.get<Opening>(`/directors/${d.id}/opening`),
  })
  const act = useMutation({
    mutationFn: (v: { path: string; body?: { approve?: boolean; reason?: string } | object }) =>
      api.post<Opening | { approved: boolean }>(v.path, v.body),
    onSuccess: (data, v) => {
      setEditing(false)
      setSendBack('')
      // The state comes from the reply itself: a read sent the moment the reply
      // lands can reach the server before its transaction has committed.
      if ('state' in data) {
        queryClient.setQueryData(['director-opening', d.id], data)
      } else {
        const approve = (v.body as { approve?: boolean }).approve
        queryClient.setQueryData<Opening>(['director-opening', d.id], (old) =>
          old
            ? {
                ...old,
                state: data.approved ? 'approved' : approve ? 'with_finance_head' : 'sent_back',
                approvals_valid: approve ? old.approvals_valid + 1 : old.approvals_valid,
                sent_back_reason: approve ? null : ((v.body as { reason?: string }).reason ?? null),
              }
            : old,
        )
      }
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['director-opening', d.id] })
        queryClient.invalidateQueries({ queryKey: ['director-history', d.id] })
        onChanged()
      }, 800)
    },
  })
  const o = opening.data
  if (opening.isLoading) return <Loading what="Loading the opening balance" />
  if (!o) return <ErrorNote error={opening.error} />

  const prepare = can('gl.journal.create')
  const decide = can('gl.journal.approve')
  const amountPaise = Math.round(Number(form.amount) * 100)
  const start = () => {
    setForm({
      amount: o.amount_paise ? String(o.amount_paise / 100) : '',
      as_at: o.as_at ?? '',
      basis: o.basis ?? '',
    })
    setEditing(true)
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 8px' }}>Opening balance</h3>
      <ErrorNote error={act.error} />

      {o.state === 'none' && !editing && (
        <Note kind="info">
          No opening balance. If {d.name} had lent the company money before these books began,
          record what was owed and the date, with where the figure comes from.
        </Note>
      )}

      {o.state !== 'none' && !editing && (
        <dl className="appr-facts">
          <div>
            <dt>Owed to {d.name}</dt>
            <dd className="strong">{formatInr(o.amount_paise ?? 0)}</dd>
          </div>
          <div>
            <dt>As at</dt>
            <dd className="date">{o.as_at}</dd>
          </div>
          <div>
            <dt>Basis</dt>
            <dd>{o.basis || '—'}</dd>
          </div>
          <div>
            <dt>Raised by</dt>
            <dd>{o.raised_by ?? '—'}</dd>
          </div>
          <div>
            <dt>{o.state === 'posted' ? 'Posted as' : 'Journal'}</dt>
            <dd className="mono">{o.journal_number ?? o.reference}</dd>
          </div>
        </dl>
      )}

      {o.state === 'with_finance_head' && (
        <Note kind="info">
          With the Finance Head for approval ({o.approvals_valid} of {o.approvals_required}{' '}
          approvals). The Book Keeper posts it once it is approved.
        </Note>
      )}
      {o.state === 'approved' && (
        <Note kind="ok">Approved. The Book Keeper posts it to the books.</Note>
      )}
      {o.state === 'sent_back' && (
        <Note kind="warn">
          Sent back{o.sent_back_reason ? `: ${o.sent_back_reason}` : ''}. Correct it and send it
          again.
        </Note>
      )}
      {o.state === 'posted' && (
        <Note kind="ok">
          In the books. A wrong opening balance is corrected with a reversing entry, not a second
          one.
        </Note>
      )}

      {editing && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            act.mutate({
              path: `/directors/${d.id}/opening`,
              body: { amount_paise: amountPaise, as_at: form.as_at, basis: form.basis },
            })
          }}
        >
          <div className="row">
            <Field label={`Owed to ${d.name} (₹)`} caption="what the company owed them">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
            </Field>
            <Field
              label="As at"
              caption={
                `${o.earliest_as_at ? `from ${o.earliest_as_at}, when these books begin, ` : ''}` +
                `${o.latest_as_at ? `to ${o.latest_as_at}, before their first entry` : ''}`
              }
            >
              <input
                type="date"
                min={o.earliest_as_at ?? undefined}
                max={o.latest_as_at ?? undefined}
                value={form.as_at}
                onChange={(e) => setForm({ ...form, as_at: e.target.value })}
                required
              />
            </Field>
          </div>
          <Field
            label="Where the figure comes from"
            caption="e.g. CA's confirmation, balance sheet 31 Mar 2026"
          >
            <input
              value={form.basis}
              onChange={(e) => setForm({ ...form, basis: e.target.value })}
              minLength={3}
              required
            />
          </Field>
          <div className="actions">
            <button
              className="primary"
              type="submit"
              disabled={
                act.isPending || !(amountPaise > 0) || !form.as_at || form.basis.trim().length < 3
              }
            >
              Send to Finance Head
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="actions">
        {prepare && !editing && (o.state === 'none' || o.state === 'sent_back') && (
          <button className={o.state === 'none' ? '' : 'primary'} onClick={start}>
            {o.state === 'none' ? 'Add opening balance' : 'Correct and send again'}
          </button>
        )}
        {prepare && o.state === 'approved' && (
          <button
            className="primary"
            disabled={act.isPending}
            onClick={() => act.mutate({ path: `/directors/${d.id}/opening/post` })}
          >
            Post opening balance
          </button>
        )}
        {decide && o.state === 'with_finance_head' && o.journal_id && (
          <>
            <button
              className="primary"
              disabled={act.isPending}
              onClick={() =>
                act.mutate({
                  path: `/approvals/manual-journal/${o.journal_id}/decide`,
                  body: { approve: true },
                })
              }
            >
              Approve opening balance
            </button>
            <input
              aria-label="What needs correcting"
              placeholder="What needs correcting?"
              value={sendBack}
              onChange={(e) => setSendBack(e.target.value)}
            />
            <button
              disabled={act.isPending || !sendBack.trim()}
              onClick={() =>
                act.mutate({
                  path: `/approvals/manual-journal/${o.journal_id}/decide`,
                  body: { approve: false, reason: sendBack },
                })
              }
            >
              Send back
            </button>
          </>
        )}
      </div>
    </div>
  )
}
